import {
  OVERLAY_GRAY,
  SECONDARY_GRAY,
  TERTIARY_GRAY
} from "@pathwright/ui/src/components/utils/colors"
import moment from "moment"
import React from "react"
import DayPicker from "react-day-picker"
import "react-day-picker/lib/style.css"
import MomentLocaleUtils, {
  formatDate,
  parseDate
} from "react-day-picker/moment"
import styled from "styled-components"
import useDidMountEffect from "../../hooks/useDidMountEffect"
import Pathicon from "../../pathicon/Pathicon"
import Tooltip from "../../tooltip/Tooltip"

const StyledDayPickerWrapper = styled.div`
  position: relative;
`
const StyledDayPicker = styled(DayPicker)`
  width: 100%;

  .DayPicker-wrapper {
    width: 100%;
    padding-bottom: 0;
  }

  .DayPicker-NavButton {
    margin-top: 0;
  }

  .DayPicker-NavButton--prev {
    margin-right: 0;
  }
  .DayPicker-NavButton--next {
    margin-right: -1.5em;
  }

  .DayPicker-Caption {
    padding-left: 1em;
  }

  .DayPicker-Caption > div {
    font-size: 1rem;
    font-weight: bold;
    color: black;
  }

  .DayPicker-Months {
    width: 100%;
  }

  .DayPicker-Month {
    width: 100%;
    margin-left: 0;
    margin-right: 0;
    margin-bottom: 15px;
  }

  .DayPicker-Weekday {
    color: #888;
  }

  .DayPicker-Day {
    border-radius: 4px;
  }

  .DayPicker-Day--today {
    color: ${SECONDARY_GRAY};
  }

  .DayPicker:not(.DayPicker--interactionDisabled)
    .DayPicker-Day:not(.DayPicker-Day--disabled):not(
      .DayPicker-Day--selected
    ):not(.DayPicker-Day--outside):hover,
  .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(
      .DayPicker-Day--outside
    ):hover {
    background-color: ${TERTIARY_GRAY};
  }

  .DayPicker:not(.DayPicker--interactionDisabled)
    .DayPicker-Day:not(.DayPicker-Day--disabled):not(
      .DayPicker-Day--selected
    ):not(.DayPicker-Day--outside):hover {
    background-color: ${OVERLAY_GRAY};
  }

  .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(
      .DayPicker-Day--outside
    ) {
    background-color: ${SECONDARY_GRAY};
    color: white;
  }
`

const StyledTooltipWrapper = styled.div`
  /* position: absolute;
  left: 0;
  top: 15px; */
  .Pathicon {
    color: #8b9898;
    margin-left: 3px;
  }
  .Overlay {
    background-color: whitesmoke;
  }
`
const StyledTooltipPopup = styled.div`
  width: 150px;
  p {
    /* font-size: 0.8rem; */
    margin: 0.3rem;
    display: flex;
    justify-content: space-between;
    :first-child {
      font-weight: normal;
    }
  }
`

export function isDayDisabled(day, disabledDays) {
  if (!day) return false
  if (!disabledDays) return false
  if (
    disabledDays.before &&
    moment(day).isBefore(disabledDays.before) &&
    !moment(day).isSame(disabledDays.before, "day")
  )
    return true
  if (
    disabledDays.after &&
    moment(day).isAfter(disabledDays.after) &&
    !moment(day).isSame(disabledDays.after, "day")
  )
    return true
  if (typeof disabledDays === "function") {
    return disabledDays(day)
  }
  return false
}

export default function ({
  value,
  onChange,
  disabledDays,
  initialMonth,
  ...rest
}) {
  const [day, setDay] = React.useState(value)
  const [month, setMonth] = React.useState(initialMonth || null)

  // handle setting day after keyup change based on disabledDays since we don't know otherwise if day is `disabled`
  const getModifiedDay = (dayModifier) => (day) => {
    const modifiedDay = dayModifier(day)
    if (!isDayDisabled(modifiedDay, disabledDays)) return modifiedDay
    // attemtpt to get closest day within non-disabled range (currently only applies to date modified by week)
    if (disabledDays) {
      if (disabledDays.before && disabledDays.after) {
        if (moment(modifiedDay).isAfter(disabledDays.before)) {
          return disabledDays.after
        } else if (moment(modifiedDay).isBefore(disabledDays.after)) {
          return disabledDays.before
        }
      }
      if (disabledDays.before) {
        return disabledDays.before
      }
    }
    // modified day is disabled and cannot be optimized to closest day
    return day
  }

  const getToday = day => moment().toDate() // prettier-ignore

  function handleDayChange(x, disabled) {
    if (disabled) return
    setDay(x)
  }

  // send value downstream
  useDidMountEffect(() => {
    if (day !== value) {
      handleDayChange(value, isDayDisabled(value, disabledDays))
    }
  }, [value])

  // send value upstream
  useDidMountEffect(() => {
    if (day !== value) {
      if (onChange) {
        onChange(day)
        setMonth(() => {
          const m = moment(day)
          if (m.isValid()) return m.toDate()
          return null
        })
      }
    }
  }, [day])

  useDidMountEffect(() => {
    if (initialMonth !== month) {
      setMonth(initialMonth)
    }
  }, [initialMonth])

  React.useEffect(() => {
    function handleKeyDown(e) {
      if (e.altKey) {
        if (e.code === "KeyT") {
          e.preventDefault()
        }
        if (e.code === "Backspace") {
          e.preventDefault()
        }
      }
    }
    function handleKeyUp(e) {
      if (e.altKey) {
        if (e.code === "KeyT") {
          e.preventDefault()
          setDay(getModifiedDay(getToday))
        }
        if (e.code === "Backspace") {
          e.preventDefault()
          setDay(null)
        }
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    window.addEventListener("keyup", handleKeyUp)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
      window.removeEventListener("keyup", handleKeyUp)
    }
  }, [disabledDays])

  return (
    <StyledDayPickerWrapper>
      <StyledDayPicker
        className="StyledDayPicker"
        {...rest}
        selectedDays={day}
        disabledDays={disabledDays}
        onDayClick={(x, { disabled }) => handleDayChange(x, disabled)}
        month={month}
        localeUtils={MomentLocaleUtils}
        formatDate={formatDate}
        parseDate={parseDate}
        format="ddd, MMM D"
        value={day || ""}
        captionElement={({ date }) => (
          <div className="DayPicker-Caption">
            <div style={{ display: "flex", alignItems: "center" }}>
              <h3 style={{ display: "inline-block", margin: 0 }}>
                {moment(date).format("MMMM YYYY")}
              </h3>
              <StyledTooltipWrapper>
                <Tooltip
                  placement="right"
                  alignment="top"
                  transition="transition.slideRightIn"
                  title={
                    <StyledTooltipPopup>
                      <p>Keyboard Shortcuts</p>
                      <p>
                        <span>Today</span>
                        <span>Alt T</span>
                      </p>
                      <p>
                        <span>Next day</span>
                        <span>→</span>
                      </p>
                      <p>
                        <span>Prev day</span>
                        <span>←</span>
                      </p>
                      <p>
                        <span>Next week</span>
                        <span>↓</span>
                      </p>
                      <p>
                        <span>Prev week</span>
                        <span>↑</span>
                      </p>
                      <p>
                        <span>Clear</span>
                        <span>Alt Del</span>
                      </p>
                    </StyledTooltipPopup>
                  }
                >
                  <Pathicon icon="info-circle" />
                </Tooltip>
              </StyledTooltipWrapper>
            </div>
          </div>
        )}
      />
    </StyledDayPickerWrapper>
  )
}
